import React, { useEffect, useMemo, useState } from 'react';
import { Activity, Gauge, History, Power, Radio, Thermometer, Zap } from 'lucide-react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import SoverdiSiteSelector from '../../soverdi-clone/components/SoverdiSiteSelector';
import { useAgriSafeSoverdiDashboard } from '../../soverdi-clone/hooks/useAgriSafeSoverdiDashboard';
import { mapOperationalState } from '../../soverdi-clone/adapters/agriSafeDashboardAdapter';
import { useAuth } from '../../contexts/AuthContext';
import { canIssuePhysicalCommands } from '../../auth/cognito';
import { usePumpCommand } from '../../hooks/usePumpCommand';
import type { IoTNode, PumpBindingView } from '../../lib/iotApi';

const UNKNOWN_OPERATIONAL_STATE = 'unknown' as const;
const PUMP_STATE_STALE_MS = 120_000;

const valueOrDash = (value: number | null | undefined, digits = 1, unit = ''): string =>
  value === null || value === undefined ? '—' : `${value.toFixed(digits)}${unit ? ` ${unit}` : ''}`;

const freshnessSuffix = (freshness: string | undefined, t: TFunction): string => {
  if (freshness === 'stale') return t('pumping.livePage.staleSuffix');
  if (freshness === 'absent') return t('pumping.livePage.missingSuffix');
  return '';
};

const ageText = (timestamp: number | null | undefined, t: TFunction): string => {
  if (!timestamp) return t('pumping.livePage.notReported');
  const seconds = Math.max(0, Math.floor((Date.now() - timestamp) / 1000));
  if (seconds < 60) return t('pumping.livePage.secondsAgo', { count: seconds });
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return t('pumping.livePage.minutesAgo', { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('pumping.livePage.hoursAgo', { count: hours });
  return t('pumping.livePage.daysAgo', { count: Math.floor(hours / 24) });
};

const MetricCard: React.FC<{ label: string; value: string; hint?: string }> = ({ label, value, hint }) => (
  <div className="rounded-xl border border-[var(--border-light)] bg-[var(--surface)] p-4 min-w-0">
    <div className="text-xs text-[var(--text-secondary)]">{label}</div>
    <div className="mt-1 text-lg font-semibold text-[var(--text-primary)] truncate" title={value}>{value}</div>
    {hint && <div className="mt-1 text-[11px] text-[var(--text-tertiary)] truncate" title={hint}>{hint}</div>}
  </div>
);

type EnvironmentSummary = NonNullable<IoTNode['environment_summary']>;

const EnvironmentMetricCard: React.FC<{
  summary: EnvironmentSummary; field: 'temperatureC' | 'humidityPercent';
  label: string; unit: string; t: TFunction;
}> = ({ summary, field, label, unit, t }) => {
  const [now, setNow] = useState(Date.now());
  // Use the server clock and elapsed client time, including on skewed devices.
  const received = useMemo(() => Date.now(), [summary.serverTime, summary.observedAt, summary.validUntil]);
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  const fresh = summary.validUntil !== null
    && summary.serverTime + Math.max(0, now - received) < summary.validUntil;
  const suffix = summary.observedAt === null ? t('pumping.livePage.missingSuffix')
    : !fresh ? t('pumping.livePage.staleSuffix')
      : summary[field] === null ? t('pumping.livePage.missingSuffix') : '';
  return <MetricCard label={label} value={valueOrDash(fresh ? summary[field] : null, 1, unit)}
    hint={`${t('pumping.livePage.relaySensor')}${suffix}`} />;
};

const commandOutcomeText = (outcome: ReturnType<ReturnType<typeof usePumpCommand>['outcomeFor']>, t: TFunction): string | null => {
  switch (outcome) {
    case 'forbidden': return t('pumping.livePage.forbidden');
    case 'conflict': return t('pumping.livePage.conflict');
    case 'uncertain': return t('pumping.livePage.uncertain');
    default: return null;
  }
};

const pumpLabel = (pump: PumpBindingView): string => pump.function || pump.pump_id;

const CgmPumpingR7: React.FC = () => {
  const { t } = useTranslation(['agrisafeR1', 'common']);
  const { user } = useAuth();
  const pumpCommand = usePumpCommand(user?.role);
  const {
    topology,
    siteModels,
    pumps,
    loading,
    loadError,
    placementUnavailable,
    bindingsUnavailable,
  } = useAgriSafeSoverdiDashboard();
  const [selectedSiteName, setSelectedSiteName] = useState('');

  const siteNames = useMemo(() => topology?.sites.map((site) => site.name) ?? [], [topology]);
  const effectiveSiteName = selectedSiteName || siteNames[0] || '';
  const siteModel = siteModels.find((model) => model.siteName === effectiveSiteName);
  const sitePumps = useMemo(
    () => pumps.filter((candidate) => candidate.site === effectiveSiteName),
    [pumps, effectiveSiteName],
  );
  const status = mapOperationalState(siteModel?.operationalState ?? UNKNOWN_OPERATIONAL_STATE);
  const statusLabels = {
    RUNNING: t('pumping.livePage.statusRUNNING'),
    STARTING: t('pumping.livePage.statusSTARTING'),
    STOPPING: t('pumping.livePage.statusSTOPPING'),
    PAUSED: t('pumping.livePage.statusPAUSED'),
    SCHEDULED: t('pumping.livePage.statusSCHEDULED'),
    FAULT: t('pumping.livePage.statusFAULT'),
    OFFLINE: t('pumping.livePage.statusOFFLINE'),
    UNKNOWN: t('pumping.livePage.statusUNKNOWN'),
    OFF: t('pumping.livePage.statusOFF'),
  };
  const pumpBound = sitePumps.length > 0;
  const commissioned = sitePumps.some((pump) => Boolean(pump.relay_channel));
  const relayChannels = siteModel?.relayChannels ?? [];
  const roleCanControl = canIssuePhysicalCommands(user?.role);
  const relayObservedAtMs = siteModel?.relayNode?.last_seen_at
    ? Date.parse(siteModel.relayNode.last_seen_at)
    : null;
  const relayObservationFresh = relayObservedAtMs !== null
    && Number.isFinite(relayObservedAtMs)
    && Date.now() - relayObservedAtMs < PUMP_STATE_STALE_MS;

  const commandEnabledFor = (pump: PumpBindingView): boolean => {
    const commandStatus = pumpCommand.statusFor(pump.pump_id);
    const commandOutcome = pumpCommand.outcomeFor(pump.pump_id);
    const commandResolved = !commandStatus
      || commandStatus === 'OBSERVED_SUCCESS'
      || commandStatus === 'OBSERVED_FAILED'
      || commandStatus === 'TIMEOUT';
    return Boolean(
      roleCanControl
      && pump.command_authorized
      && pump.relay_channel
      && commandResolved
      && commandOutcome === null
      && pumpCommand.pending === null
      && !loadError
      && !placementUnavailable
      && !bindingsUnavailable,
    );
  };

  const observedStateFor = (pump: PumpBindingView): { label: string; className: string } => {
    if (!pump.relay_channel) {
      return { label: t('pumping.livePage.relayUNKNOWN'), className: 'text-[var(--color-warning)]' };
    }
    const relay = relayChannels.find((candidate) => candidate.channel === pump.relay_channel);
    if (!relay || relay.on === null) {
      return { label: t('pumping.livePage.relayUNKNOWN'), className: 'text-[var(--color-warning)]' };
    }
    if (!relayObservationFresh) {
      return { label: t('pumping.livePage.relaySTALE'), className: 'text-[var(--color-warning)]' };
    }
    return relay.on
      ? { label: t('pumping.livePage.relayON'), className: 'text-[var(--color-success)]' }
      : { label: t('pumping.livePage.relayOFF'), className: 'text-[var(--text-secondary)]' };
  };

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><div className="w-10 h-10 rounded-full border-2 border-[var(--color-primary)] border-t-transparent animate-spin" /></div>;
  }

  if (!topology) {
    return <div className="min-h-[60vh] flex items-center justify-center text-sm text-[var(--text-secondary)]">{t('pumping.livePage.unavailable')}</div>;
  }

  return (
    <div className="space-y-5 pb-[calc(7.5rem+env(safe-area-inset-bottom,0px))] lg:pb-6" data-testid="soverdi-pumping-r7">
      <SoverdiSiteSelector sites={siteNames} selected={effectiveSiteName} onSelect={setSelectedSiteName} />

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-[var(--color-primary)] mb-1"><Zap className="w-5 h-5" /><span className="text-sm font-medium">{t('pumping.livePage.title')}</span></div>
          <h1 className="text-xl sm:text-2xl font-semibold text-[var(--text-primary)]">{effectiveSiteName}</h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1">{t('pumping.livePage.subtitle')}</p>
        </div>
        <span className="w-fit px-3 py-1.5 rounded-full text-xs font-medium bg-[var(--bg-tertiary)] text-[var(--text-secondary)]">
          {statusLabels[status]}
        </span>
      </div>

      {loadError && (
        <div className="rounded-xl border border-[var(--color-warning)]/30 bg-[var(--color-warning)]/10 px-4 py-3 text-sm text-[var(--text-secondary)]">
          {t('pumping.livePage.partialData')}
        </div>
      )}

      <section className="rounded-2xl border border-[var(--color-primary)]/25 bg-[var(--surface)] p-4 sm:p-5">
        <div className="mb-4">
          <h2 className="font-semibold text-[var(--text-primary)]">{t('pumping.livePage.commands')}</h2>
        </div>

        {sitePumps.length > 0 ? (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4" data-testid="pumping-motor-list">
            {sitePumps.map((pump) => {
              const observed = observedStateFor(pump);
              const commandStatus = pumpCommand.statusFor(pump.pump_id);
              const commandOutcome = pumpCommand.outcomeFor(pump.pump_id);
              const outcomeText = commandOutcomeText(commandOutcome, t);
              const commandEnabled = commandEnabledFor(pump);
              return (
                <article
                  key={pump.pump_id}
                  className="rounded-xl border border-[var(--border-light)] bg-[var(--bg-tertiary)] p-4"
                  data-testid={`pumping-motor-${pump.pump_id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-semibold text-[var(--text-primary)]">{pumpLabel(pump)}</h3>
                      <div className="mt-1 text-xs text-[var(--text-secondary)]">
                        {pump.relay_node_id} · {pump.relay_channel || t('pumping.livePage.channelUnconfirmed')}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`text-sm font-semibold ${observed.className}`} data-testid={`pumping-state-${pump.pump_id}`}>
                        {observed.label}
                      </div>
                      <div className="text-[11px] text-[var(--text-tertiary)]">
                        {ageText(relayObservedAtMs, t)}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 mt-4">
                    <button
                      type="button"
                      onClick={() => pumpCommand.requestCommand(pump, 'start')}
                      disabled={!commandEnabled}
                      data-testid={`pumping-start-${pump.pump_id}`}
                      className="min-h-[50px] rounded-xl bg-[var(--color-success)] text-white font-semibold disabled:cursor-not-allowed disabled:opacity-40 flex items-center justify-center gap-2"
                    >
                      <Power className="w-4 h-4" />{t('dashboard.pump.start')}
                    </button>
                    <button
                      type="button"
                      onClick={() => pumpCommand.requestCommand(pump, 'stop')}
                      disabled={!commandEnabled}
                      data-testid={`pumping-stop-${pump.pump_id}`}
                      className="min-h-[50px] rounded-xl bg-[var(--color-danger)] text-white font-semibold disabled:cursor-not-allowed disabled:opacity-40 flex items-center justify-center gap-2"
                    >
                      <Power className="w-4 h-4" />{t('dashboard.pump.stop')}
                    </button>
                  </div>

                  <div className="mt-3 text-xs text-[var(--text-tertiary)]">
                    {!roleCanControl
                      ? t('pumping.livePage.roleDenied')
                      : !pump.command_authorized
                        ? t('pumping.livePage.motorDenied')
                        : pump.relay_channel
                          ? t('pumping.livePage.confirmRequired')
                          : t('pumping.livePage.physicalUnconfirmed')}
                  </div>

                  {commandStatus && (
                    <div className="mt-3 rounded-xl border border-[var(--border-light)] bg-[var(--surface)] px-3 py-2 text-xs text-[var(--text-secondary)]" data-testid={`pumping-command-status-${pump.pump_id}`}>
                      {t('pumping.livePage.commandStatus')} <strong>{commandStatus}</strong> · {t('pumping.livePage.notProof')}
                    </div>
                  )}
                  {outcomeText && (
                    <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" role="alert" data-testid={`pumping-command-outcome-${pump.pump_id}`}>
                      {outcomeText}
                    </div>
                  )}
                  {pumpCommand.canRecheck?.(pump.pump_id) && (
                    <button type="button" className="mt-2 min-h-[44px] underline"
                      onClick={() => pumpCommand.recheckCommand(pump)} data-testid={`pump-recheck-${pump.pump_id}`}>
                      {t('common:refresh')}
                    </button>
                  )}
                </article>
              );
            })}
          </div>
        ) : (
          <div className="rounded-xl border border-[var(--border-light)] bg-[var(--bg-tertiary)] px-4 py-3 text-sm text-[var(--text-secondary)]">
            {bindingsUnavailable
              ? `${t('common:availabilityUnavailable')} — ${t('common:commandUnavailableDetail')}`
              : t('pumping.livePage.noBinding')}
          </div>
        )}
      </section>

      <section className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <MetricCard label={t('pumping.livePage.pressure')} value="— bar" hint={t('pumping.livePage.noMeasure')} />
        <MetricCard label={t('pumping.livePage.flow')} value="— m³/h" hint={t('pumping.livePage.noMeasure')} />
        {siteModel?.relayNode?.environment_summary ? <React.Fragment key={siteModel.relayNode.id}>
          <EnvironmentMetricCard summary={siteModel.relayNode.environment_summary} field="temperatureC"
            label={t('pumping.livePage.airTemperature')} unit="°C" t={t} />
          <EnvironmentMetricCard summary={siteModel.relayNode.environment_summary} field="humidityPercent"
            label={t('pumping.livePage.airHumidity')} unit="%" t={t} />
        </React.Fragment> : <>
          <MetricCard label={t('pumping.livePage.airTemperature')} value={valueOrDash(siteModel?.relayAirTemperature.value, 1, '°C')} hint={`${t('pumping.livePage.relaySensor')}${freshnessSuffix(siteModel?.relayAirTemperature.freshness, t)}`} />
          <MetricCard label={t('pumping.livePage.airHumidity')} value={valueOrDash(siteModel?.relayAirHumidity.value, 1, '%')} hint={`${t('pumping.livePage.relaySensor')}${freshnessSuffix(siteModel?.relayAirHumidity.freshness, t)}`} />
        </>}
        <MetricCard label={t('pumping.livePage.lastCommunication')} value={ageText(siteModel?.lastCommunicationMs, t)} />
        <MetricCard label={t('pumping.livePage.commissioning')} value={commissioned ? t('pumping.livePage.configuredMotors', { count: sitePumps.length }) : pumpBound ? t('pumping.livePage.readyForField') : t('pumping.livePage.notApplicable')} />
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.55fr)_minmax(300px,.75fr)] gap-4">
        <div className="rounded-xl border border-[var(--border-light)] bg-[var(--surface)] p-4 sm:p-5 min-h-[330px]">
          <div className="flex items-center gap-2"><History className="w-4 h-4 text-[var(--color-primary)]" /><h2 className="font-semibold text-[var(--text-primary)]">{t('pumping.livePage.history')}</h2></div>
          <p className="text-xs text-[var(--text-secondary)] mt-1">{t('pumping.livePage.historyUnavailable')}</p>
          <div className="h-[250px] flex items-center justify-center text-sm text-[var(--text-tertiary)]">{t('pumping.livePage.unavailable')}</div>
        </div>

        <div className="rounded-xl border border-[var(--border-light)] bg-[var(--surface)] p-4">
          <div className="flex items-center gap-2 mb-3"><Radio className="w-4 h-4 text-[var(--color-primary)]" /><h2 className="font-semibold text-[var(--text-primary)]">{t('pumping.livePage.relays')}</h2></div>
          {relayChannels.length === 0 ? (
            <div className="py-8 text-center text-sm text-[var(--text-tertiary)]">{t('pumping.livePage.noRelay')}</div>
          ) : (
            <div className="space-y-2">
              {relayChannels.map((relay) => {
                const stale = !relayObservationFresh;
                const stateLabel = stale ? t('pumping.livePage.relaySTALE') : relay.on === true ? t('pumping.livePage.on') : relay.on === false ? t('pumping.livePage.off') : t('pumping.livePage.unknown');
                const stateClass = stale || relay.on === null ? 'text-[var(--color-warning)]' : relay.on === true ? 'text-[var(--color-success)]' : 'text-[var(--text-secondary)]';
                return (
                  <div key={relay.channel} className="rounded-lg border border-[var(--border-light)] p-3 flex items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-[var(--text-primary)]">{relay.channel}</div>
                      <div className="text-[11px] text-[var(--text-tertiary)]">{t('pumping.livePage.relayEvidence')}</div>
                    </div>
                    <span className={`text-xs font-semibold ${stateClass}`}>{stateLabel}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <section className="rounded-xl border border-[var(--border-light)] bg-[var(--surface)] p-4 sm:p-5">
        <div className="flex items-center gap-2 mb-3"><Activity className="w-4 h-4 text-[var(--color-primary)]" /><h2 className="font-semibold text-[var(--text-primary)]">{t('pumping.livePage.diagnostic')}</h2></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <div className="flex justify-between gap-3 border-b border-[var(--border-light)] py-2"><span className="text-[var(--text-secondary)]">{t('pumping.livePage.relay')}</span><strong>{siteModel?.relayNode?.id ?? t('pumping.livePage.unassigned')}</strong></div>
          <div className="flex justify-between gap-3 border-b border-[var(--border-light)] py-2"><span className="text-[var(--text-secondary)]">{t('pumping.livePage.actuators')}</span><strong>{sitePumps.map((pump) => `${pump.relay_channel ?? '?'} · ${pumpLabel(pump)}`).join(' / ') || t('pumping.livePage.none')}</strong></div>
          <div className="flex justify-between gap-3 border-b border-[var(--border-light)] py-2"><span className="text-[var(--text-secondary)]">{t('pumping.livePage.interfaces')}</span><strong>{Array.from(new Set(sitePumps.map((pump) => pump.interface_type || t('pumping.livePage.toConfirm')))).join(' / ') || t('pumping.livePage.toConfirm')}</strong></div>
          <div className="flex justify-between gap-3 border-b border-[var(--border-light)] py-2"><span className="text-[var(--text-secondary)]">{t('pumping.livePage.evidence')}</span><strong>{siteModel?.stateEvidence ?? 'none'}</strong></div>
        </div>
      </section>

      <div className="flex items-center justify-center gap-2 text-xs text-[var(--text-tertiary)]"><Gauge className="w-3.5 h-3.5" /><Thermometer className="w-3.5 h-3.5" />{t('pumping.livePage.footer')}</div>

      {pumpCommand.pending && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          data-testid="pump-confirm-dialog"
        >
          <div className="w-full max-w-md rounded-2xl bg-[var(--surface)] p-5 shadow-lg">
            <h2 className="text-lg font-semibold text-[var(--text-primary)]" data-testid="pump-confirm-title">
              {pumpCommand.pending.action === 'start'
                ? t('dashboard.pump.confirmStartTitle')
                : t('dashboard.pump.confirmStopTitle')}
            </h2>
            <p className="mt-2 text-sm text-[var(--text-primary)]">
              {pumpCommand.pending.pump.function} · {pumpCommand.pending.pump.site}
            </p>

            {pumpCommand.pending.requiresHydraulicConfirmation && (
              <div
                className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                data-testid="pump-hydraulic-confirmation"
              >
                <div className="font-semibold">{t('dashboard.pump.manualHydraulicTitle')}</div>
                <div className="mt-1">{t('dashboard.pump.manualHydraulicConfirmation')}</div>
              </div>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={pumpCommand.cancelConfirmation}
                className="min-h-[44px] rounded-xl border border-[var(--border-light)] px-4 py-2 text-sm font-semibold text-[var(--text-primary)]"
              >
                {t('dashboard.pump.cancel')}
              </button>
              <button
                type="button"
                onClick={pumpCommand.confirmCommand}
                data-testid="pump-confirm-proceed"
                className="min-h-[44px] rounded-xl bg-[var(--color-primary)] px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
              >
                {t('dashboard.pump.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CgmPumpingR7;
